import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
//Components
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
//Icons
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
//Utils
import propertiesApi from '../../api/propertiesApi';
import c from '../../constants/Constants';
import tenantUtil from '../../utils/tenantUtil';
import { getPropertyPublicUrl } from '../../utils/domainUtil';
import { copyToClipboard } from '../../utils/clipboardUtil';
import {
  buildPhotoFilename,
  downloadRemoteFile
} from '../../utils/downloadUtil';
import {
  buildPropertyCaption,
  resolveAttributes,
  resolveOperations
} from '../../utils/publicationCaption';

//Instagram corta los pies de foto en 2200 caracteres
const MAX_CAPTION_LENGTH = 2200;

const useStyles = makeStyles(theme => ({
  section: {
    marginTop: theme.spacing(3)
  },
  photo: {
    width: '100%',
    height: '140px',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    border: '1px solid rgba(0, 0, 0, 0.2)'
  },
  photoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  photoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    opacity: 0,
    transition: 'opacity 150ms',
    '&:hover': {
      opacity: 1
    }
  },
  photoOverlayVisible: {
    opacity: 1
  }
}));

function PublicationKitModal(props) {
  const classes = useStyles();
  const { open, setOpen, property } = props;

  //Store
  const propertyTypes = useSelector(state => state.propertyTypes);
  const operationTypes = useSelector(state => state.operationTypes);
  const propertyOperations = useSelector(state => state.propertyOperations);
  const attributes = useSelector(state => state.attributes);
  const attributesGroups = useSelector(state => state.attributesGroups);
  const attributesValues = useSelector(state => state.attributesValues);
  const propertiesAttributesValues = useSelector(
    state => state.propertiesAttributesValues
  );
  const tenants = useSelector(state => state.tenants);
  const domains = useSelector(state => state.domains);

  //State
  const [caption, setCaption] = useState('');
  const [edited, setEdited] = useState(false);
  const [photos, setPhotos] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [photosError, setPhotosError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [message, setMessage] = useState(null);

  const contentRef = useRef(null);

  const tenant =
    tenants.update.activo?.domains?.length > 0
      ? tenants.update.activo
      : tenantUtil.getTenant()
      ? tenants.byId.tenants[tenantUtil.getTenant()]
      : null;

  const publicUrl = getPropertyPublicUrl(
    tenant,
    domains.byId.domains,
    property?.id
  );

  //El listado ya trae título, descripción, operaciones y atributos, así que el
  //texto se arma al instante: sólo las fotos esperan la red.
  const generatedCaption = useMemo(
    () =>
      buildPropertyCaption({
        property,
        propertyTypeName:
          propertyTypes.byId.propertyTypes[property?.idPropertyType]?.nameType,
        propertySubTypeName:
          propertyTypes.byId.propertyTypes[property?.idPropertySubType]
            ?.nameType,
        operations: resolveOperations(property, {
          propertyOperations,
          operationTypes
        }),
        attributes: resolveAttributes(property, {
          attributes,
          attributesGroups,
          attributesValues,
          propertiesAttributesValues
        }),
        publicUrl
      }),
    [
      property,
      propertyTypes,
      propertyOperations,
      operationTypes,
      attributes,
      attributesGroups,
      attributesValues,
      propertiesAttributesValues,
      publicUrl
    ]
  );

  //Sólo se regenera mientras el usuario no haya tocado el texto
  useEffect(() => {
    if (!edited) setCaption(generatedCaption);
  }, [generatedCaption, edited]);

  const loadPhotos = useCallback(() => {
    let cancelled = false;
    setLoadingPhotos(true);
    setPhotosError(null);

    //Ojo: no usar dispatch(fetchProperty(id)). Esa acción prende
    //properties.byId.isFetching y PropertiesList reemplaza toda la lista por
    //skeletons, con lo que la card dueña de este modal se desmonta y el modal
    //se cierra solo. Por eso se llama a la API directo, con estado local.
    propertiesApi
      .getOne(property.id)
      .then(response =>
        response.status >= 400 ? Promise.reject(response) : response.json()
      )
      .then(data => {
        if (cancelled) return;
        const list = (data?.asset || [])
          .filter(asset => asset && asset.type === 'Photo')
          .sort(
            (a, b) =>
              Number(b.mainImage || 0) - Number(a.mainImage || 0) ||
              Number(a.order || 0) - Number(b.order || 0)
          );
        setPhotos(list);
      })
      .catch(() => {
        if (!cancelled) setPhotosError('No pudimos cargar las fotos.');
      })
      .finally(() => {
        if (!cancelled) setLoadingPhotos(false);
      });

    return () => {
      cancelled = true;
    };
  }, [property.id]);

  useEffect(() => loadPhotos(), [loadPhotos]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeCaption = e => {
    setEdited(true);
    setCaption(e.target.value);
  };

  const handleResetCaption = () => {
    setEdited(false);
    setCaption(generatedCaption);
  };

  const handleCopy = async (text, successMessage) => {
    const copied = await copyToClipboard(text, contentRef.current);
    setMessage(
      copied
        ? { severity: 'success', text: successMessage }
        : {
            severity: 'error',
            text: 'No pudimos copiar. Seleccioná el texto y copialo a mano.'
          }
    );
  };

  const handleDownloadPhoto = async (asset, index) => {
    setDownloading(prev => ({ ...prev, [asset.id]: true }));
    try {
      await downloadRemoteFile(
        c.API_HOST + asset.path,
        buildPhotoFilename(property, asset, index)
      );
    } catch (e) {
      setMessage({
        severity: 'error',
        text: 'No pudimos descargar la foto. Intentá de nuevo.'
      });
    } finally {
      setDownloading(prev => {
        const next = { ...prev };
        delete next[asset.id];
        return next;
      });
    }
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
      fullWidth
      maxWidth={'md'}
      scroll={'paper'}
    >
      <DialogTitle id="simple-dialog-title">Kit de publicación</DialogTitle>
      <DialogContent dividers ref={contentRef}>
        <Typography variant={'overline'} component={'div'}>
          Texto para publicar
        </Typography>
        <TextField
          id="caption"
          multiline
          minRows={12}
          fullWidth
          variant="outlined"
          margin={'dense'}
          value={caption}
          onChange={handleChangeCaption}
          error={caption.length > MAX_CAPTION_LENGTH}
          helperText={`${caption.length} / ${MAX_CAPTION_LENGTH} caracteres`}
        />
        <Box
          display={'flex'}
          justifyContent={'flex-end'}
          gridColumnGap={'8px'}
          flexWrap={'wrap'}
        >
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleResetCaption}
            disabled={!edited}
            color="primary"
          >
            Restaurar texto
          </Button>
          <Button
            variant={'contained'}
            color="primary"
            startIcon={<FileCopyIcon />}
            onClick={() => handleCopy(caption, 'Texto copiado')}
            disabled={!caption}
          >
            Copiar texto
          </Button>
        </Box>

        <Box className={classes.section}>
          <Typography variant={'overline'} component={'div'}>
            Link de la propiedad
          </Typography>
          {publicUrl ? (
            <Box display={'flex'} alignItems={'center'} gridColumnGap={'8px'}>
              <TextField
                id="publicUrl"
                fullWidth
                size={'small'}
                variant="outlined"
                value={publicUrl}
                InputProps={{ readOnly: true }}
              />
              <Tooltip title="Copiar link">
                <IconButton
                  aria-label="copiar link"
                  onClick={() => handleCopy(publicUrl, 'Link copiado')}
                >
                  <FileCopyIcon color="secondary" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant={'caption'} component={'div'}>
              Tu sitio web todavía no está publicado, por eso esta propiedad no
              tiene link para compartir.
            </Typography>
          )}
        </Box>

        <Box className={classes.section}>
          <Typography variant={'overline'} component={'div'}>
            Fotos
          </Typography>
          {!loadingPhotos && !photosError && photos?.length > 0 && (
            <Typography variant={'caption'} component={'div'}>
              Hacé click en una foto para descargarla.
            </Typography>
          )}

          {loadingPhotos && (
            <Grid container spacing={2}>
              {[...Array(6).keys()].map(i => (
                <Grid item xs={6} sm={4} md={3} key={'kitskeleton' + i}>
                  <Skeleton variant={'rect'} height={140} />
                </Grid>
              ))}
            </Grid>
          )}

          {!loadingPhotos && photosError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadPhotos}>
                  Reintentar
                </Button>
              }
            >
              {photosError}
            </Alert>
          )}

          {!loadingPhotos && !photosError && photos?.length === 0 && (
            <Typography variant={'caption'} component={'div'}>
              Esta propiedad todavía no tiene fotos cargadas.
            </Typography>
          )}

          {!loadingPhotos && !photosError && photos?.length > 0 && (
            <Grid container spacing={2}>
              {photos.map((asset, index) => (
                <Grid item xs={6} sm={4} md={3} key={'kitphoto' + asset.id}>
                  <ButtonBase
                    className={classes.photo}
                    focusRipple
                    disabled={!!downloading[asset.id]}
                    onClick={() => handleDownloadPhoto(asset, index)}
                  >
                    <img
                      alt={'foto de la propiedad'}
                      className={classes.photoImg}
                      src={
                        c.API_HOST + (asset.pathThumbnail || asset.path || '')
                      }
                    />
                    <Box
                      className={
                        downloading[asset.id]
                          ? `${classes.photoOverlay} ${classes.photoOverlayVisible}`
                          : classes.photoOverlay
                      }
                    >
                      {downloading[asset.id] ? (
                        <CircularProgress size={24} color={'inherit'} />
                      ) : (
                        <GetAppIcon />
                      )}
                    </Box>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>

      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {message && (
          <Alert
            onClose={() => setMessage(null)}
            severity={message.severity}
            variant="filled"
          >
            {message.text}
          </Alert>
        )}
      </Snackbar>
    </Dialog>
  );
}

PublicationKitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  property: PropTypes.object.isRequired
};

export default PublicationKitModal;
